/* ==========================================================================
   Wall Art PDP — <wall-art-configurator> custom element.
   Self-contained step personaliser (wac- namespace). Pairs with the snippet/CSS.
   ========================================================================== */
(function () {
  'use strict';

  var TAG = 'wall-art-configurator';

  class WallArtConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.section = this.closest('.pp-pdp') || document;
      this.currency = this.dataset.currency || 'USD';

      var dataEl = this.querySelector('[data-wac-variants]');
      try {
        this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
      } catch (e) {
        this.variants = [];
      }

      var selId = parseInt(this.dataset.selectedVariant, 10);
      this.current =
        this.findById(selId) ||
        this.firstAvailable() ||
        this.variants[0] ||
        null;
      this.baseline = this.current ? this.current.o.slice() : [];

      // Step navigation state
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-wac-panel]'));
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-wac-tab]'));
      this.tabsEl = this.querySelector('.wac__tabs');
      this.total = parseInt(this.dataset.totalSteps, 10) || this.panels.length;
      this.bar = this.querySelector('[data-wac-progress-bar]');
      this.stepCurrentEl = this.querySelector('[data-wac-step-current]');
      this.hintEl = this.querySelector('[data-wac-progress-hint]');
      this.nav = this.querySelector('.wac__nav');
      this.backBtn = this.nav && this.nav.querySelector('[data-wac-back]');
      this.nextBtn = this.nav && this.nav.querySelector('[data-wac-next]');
      // Primary Add-to-cart plus an optional "Add to cart + Make another" button.
      this.atcBtns = this.nav ? Array.prototype.slice.call(this.nav.querySelectorAll('[data-wac-atc]')) : [];
      this.atcBtn = this.atcBtns[0] || null;
      this.addedEl = this.querySelector('[data-wac-added]');
      // Afterpay wrapper — shown only on the final step (below Add to cart).
      this.afterpayEl = this.querySelector('[data-wac-afterpay]');
      // Happiness-guarantee box — shown only on the final step (below the buttons).
      this.guaranteeEl = this.querySelector('[data-wac-guarantee]');
      this.step = 0;

      // Running-total summary: base + per-extra lines feeding the live total.
      this.baseEl = this.querySelector('[data-wac-base]');
      this.totalEl = this.querySelector('[data-wac-total]');
      // Compare-at "was" total + saving (present only when the setting is on).
      this.compareHolder = this.querySelector('[data-wac-compare-holder]');
      this.compareMoney = this.querySelector('[data-wac-compare-money]');
      this.saveHolder = this.querySelector('[data-wac-save-holder]');
      this.saveMoney = this.querySelector('[data-wac-save-money]');
      this.extraInputs = Array.prototype.slice.call(this.querySelectorAll('[data-wac-extra]'));
      // Extra Prints (optional step) — count bumps the main artwork line quantity.
      this.copyInputs = Array.prototype.slice.call(this.querySelectorAll('[data-wac-copies]'));
      this.copiesRow = this.querySelector('[data-wac-copies-row]');
      // Snapshot for "Make another": starting variant + initially-checked inputs.
      this._initialVariantId = this.current ? this.current.id : null;
      this._initialCheckedInputs = Array.prototype.slice.call(
        this.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked')
      );

      this.freeshipEl = this.querySelector('[data-wac-freeship]');

      // Gift message step (present only when custom.gift_message_product is set).
      this.giftToggle = this.querySelector('[data-wac-gift-toggle]');
      this.giftSummaryRow = this.querySelector('[data-wac-summary-gift]');
      this.ftSummaryRow = this.querySelector('[data-wac-summary-fasttrack]');
      this.giftMessageEl = this.querySelector('[data-wac-gift-message]');
      this.giftCountEl = this.querySelector('[data-wac-gift-count]');
      this.giftPreviewEl = this.querySelector('[data-wac-gift-preview]');

      // Index of the Upload step, so Next there opens the photo confirm popup.
      this.uploadStep = -1;
      for (var p = 0; p < this.panels.length; p++) {
        if (this.panels[p].getAttribute('data-step-key') === 'upload') {
          this.uploadStep = p;
          break;
        }
      }

      // Photo uploaders are owned by the store's UploadKit app; the theme reads
      // their state for the gate but never sets the count (see allPhotosDone).
      this._onChange = this.onChange.bind(this);
      this._onClick = this.onClick.bind(this);
      this._onInput = this.onInput.bind(this);
      this._onKey = this.onKey.bind(this);
      this.addEventListener('change', this._onChange);
      this.addEventListener('click', this._onClick);
      this.addEventListener('input', this._onInput);
      document.addEventListener('keydown', this._onKey);
      // Fast Track (sibling block) flips independently — re-total when it changes.
      this._onFtChange = this.recalcSummary.bind(this);
      document.addEventListener('fasttrack:change', this._onFtChange);

      this.refreshAvailability();
      this.showStep(false);
      this.recalcSummary();
      // Hydrate the uploader early so it's ready by the Upload step.
      this.ensureUploader();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('input', this._onInput);
      document.removeEventListener('keydown', this._onKey);
      if (this._onFtChange) document.removeEventListener('fasttrack:change', this._onFtChange);
      if (this._uploadObserver) this._uploadObserver.disconnect();
      if (this._uploadPoll) clearInterval(this._uploadPoll);
      this._initialized = false;
    }

    /* --- Step navigation --- */

    onKey(e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) return;
      var open = this.querySelector('.wac-modal.is-open');
      if (open) this.closeOverlay(open);
    }

    onClick(e) {
      // --- Size Chart + Photo Guide modals + confirm popup ---
      if (e.target.closest('[data-wac-size-chart]')) {
        e.preventDefault();
        this.openOverlay(this.querySelector('[data-wac-size-chart-modal]'));
        return;
      }
      if (e.target.closest('[data-wac-photo-guide]')) {
        e.preventDefault();
        this.openOverlay(this.querySelector('[data-wac-modal]'));
        return;
      }
      if (e.target.closest('[data-wac-modal-close]')) {
        this.closeOverlay(e.target.closest('.wac-modal'));
        return;
      }
      if (e.target.closest('[data-wac-upload-now]')) {
        this.closeOverlay(this.querySelector('[data-wac-upload-confirm]'));
        // Global "upload now" — open the first uploader still missing a photo.
        this.openFirstPendingUploader();
        return;
      }
      if (e.target.closest('[data-wac-upload-later]')) {
        this._skipPhoto = true;
        this.setPhotoProp();
        if (this.uploadStep >= 0) this.clearError(this.panels[this.uploadStep]);
        this.closeOverlay(this.querySelector('[data-wac-upload-confirm]'));
        // Advance to the next step if there is one; if Upload is the final step
        // the popup was reached via Add to cart, so complete that now (go() would
        // no-op past the last step and leave the shopper stuck).
        if (this.uploadStep >= 0) {
          if (this.uploadStep + 1 < this.total) this.go(this.uploadStep + 1);
          // Replay the ATC intent that opened this popup (preserves Make another).
          else this.onAtc(!!this._pendingMakeAnother, this._pendingAtcBtn);
        }
        return;
      }
      // Let the app's own UploadKit button(s) handle their own clicks.
      if (e.target.closest('[data-wac-uploadkit]')) return;

      // --- Add to cart (final step) ---
      var atcBtn = e.target.closest('[data-wac-atc]');
      if (atcBtn) {
        e.preventDefault();
        this.onAtc(atcBtn.hasAttribute('data-wac-atc-make-another'), atcBtn);
        return;
      }

      // --- Step nav ---
      if (e.target.closest('[data-wac-next]')) {
        // Upload step: Next without a photo opens the "no photo yet" popup first.
        if (this.step === this.uploadStep && !this.allPhotosDone() && !this._skipPhoto) {
          this.openOverlay(this.querySelector('[data-wac-upload-confirm]'));
          return;
        }
        this.go(this.step + 1);
        return;
      }
      if (e.target.closest('[data-wac-back]')) {
        this.go(this.step - 1);
        return;
      }
      var tab = e.target.closest('[data-wac-tab]');
      if (tab) {
        var i = parseInt(tab.getAttribute('data-step-index'), 10);
        if (!isNaN(i)) this.go(i);
      }
    }

    go(i) {
      if (i < 0 || i >= this.total || i === this.step) return;
      // Forward: every required step before the target must be satisfied. Back: free.
      if (i > this.step) {
        var invalid = this.firstInvalidBefore(i);
        if (invalid !== -1) {
          this.jumpToInvalid(invalid);
          return;
        }
      }
      this.step = i;
      this.showStep(true);
    }

    // Jump to an offending step: Upload shows the "no photo yet" popup, others
    // show an inline error.
    jumpToInvalid(idx) {
      this.step = idx;
      this.showStep(true);
      if (this.panels[idx].getAttribute('data-step-key') === 'upload') {
        this.openOverlay(this.querySelector('[data-wac-upload-confirm]'));
      } else {
        this.showError(this.panels[idx], this.stepError(idx));
      }
    }

    /* --- Required-step validation --- */

    stepValid(idx) {
      return this.stepError(idx) === null;
    }

    // Null when satisfied, otherwise the message to show.
    stepError(idx) {
      var panel = this.panels[idx];
      if (!panel) return null;
      var key = panel.getAttribute('data-step-key');

      // Upload: needs a photo, or "send later" chosen.
      if (key === 'upload') {
        if (this.allPhotosDone() || this._skipPhoto) return null;
        return this.dataset.wacUploadMsg || 'Please upload a photo, or choose to send it later.';
      }

      // Gift message: required only when opted in with an empty message.
      if (key === 'gift_message') {
        var toggle = panel.querySelector('[data-wac-gift-toggle]');
        if (toggle && toggle.checked) {
          var msgEl = panel.querySelector('[data-wac-gift-message]');
          var msg = msgEl ? msgEl.value.trim() : '';
          if (!msg) return this.dataset.wacGiftMsg || 'Please enter your gift message.';
        }
        return null;
      }

      // Required variant steps need a checked radio.
      if (
        panel.getAttribute('data-wac-required') === 'true' &&
        !panel.querySelector('input[type="radio"]:checked')
      ) {
        return this.dataset.wacValidationMsg || 'Please make a selection to continue.';
      }
      return null;
    }

    firstInvalidBefore(target) {
      for (var idx = 0; idx < target; idx++) {
        if (!this.stepValid(idx)) return idx;
      }
      return -1;
    }

    showError(panel, message) {
      if (!panel) return;
      panel.classList.add('has-error');
      var err = panel.querySelector('[data-wac-error]');
      if (!err) {
        err = document.createElement('p');
        err.className = 'wac-panel__error';
        err.setAttribute('data-wac-error', '');
        panel.appendChild(err);
      }
      err.textContent =
        message || this.dataset.wacValidationMsg || 'Please make a selection to continue.';
      err.hidden = false;
    }

    clearError(panel) {
      if (!panel) return;
      panel.classList.remove('has-error');
      var err = panel.querySelector('[data-wac-error]');
      if (err) err.hidden = true;
    }

    showStep(scroll) {
      var s = this.step;
      var total = this.total;

      this.panels.forEach(function (p, idx) {
        var on = idx === s;
        p.classList.toggle('is-active', on);
        p.hidden = !on;
      });

      this.tabs.forEach(function (t, idx) {
        var active = idx === s;
        t.classList.toggle('is-active', active);
        t.classList.toggle('is-complete', idx < s);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.tabIndex = active ? 0 : -1;
      });

      if (this.bar) this.bar.style.width = ((s + 1) / total * 100).toFixed(2) + '%';
      if (this.stepCurrentEl) this.stepCurrentEl.textContent = s + 1;
      if (this.hintEl) {
        var togo = total - (s + 1);
        this.hintEl.textContent =
          togo <= 0 ? 'Final step' : togo + ' ' + (togo === 1 ? 'step' : 'steps') + ' to go';
      }

      // Final step swaps Next for Add to cart (+ afterpay/guarantee); Back hidden on step 1.
      var onLast = s === total - 1;
      if (this.backBtn) this.backBtn.hidden = s === 0;
      if (this.nextBtn) this.nextBtn.hidden = onLast;
      (this.atcBtns || []).forEach(function (b) { b.hidden = !onLast; });
      if (this.afterpayEl) this.afterpayEl.hidden = !onLast;
      if (this.guaranteeEl) this.guaranteeEl.hidden = !onLast;
      if (this.nav) this.nav.hidden = false;

      if (s === this.uploadStep) this.ensureUploader();

      if (scroll && this.tabs[s] && this.tabs[s].scrollIntoView) {
        this.tabs[s].scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }
    }

    /* --- Variant matching --- */

    findById(id) {
      for (var i = 0; i < this.variants.length; i++) {
        if (this.variants[i].id === id) return this.variants[i];
      }
      return null;
    }

    firstAvailable() {
      for (var i = 0; i < this.variants.length; i++) {
        if (this.variants[i].a) return this.variants[i];
      }
      return null;
    }

    // Baseline options, overlaid with any checked radio in an option panel.
    getSelectedOptions() {
      var selected = this.baseline.slice();
      var panels = this.querySelectorAll('[data-wac-panel][data-wac-option-position]');
      Array.prototype.forEach.call(panels, function (panel) {
        var pos = parseInt(panel.getAttribute('data-wac-option-position'), 10) - 1;
        var checked = panel.querySelector('input[type="radio"]:checked');
        if (checked) selected[pos] = checked.value;
      });
      return selected;
    }

    matchExact(sel) {
      for (var i = 0; i < this.variants.length; i++) {
        var v = this.variants[i];
        if (v.o.length !== sel.length) continue;
        var ok = true;
        for (var j = 0; j < sel.length; j++) {
          if (v.o[j] !== sel[j]) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }

    onChange(e) {
      if (!e.target.matches) return;

      // Extras toggle: re-total only.
      if (e.target.matches('[data-wac-extra]')) {
        this.recalcSummary();
        return;
      }

      // Extra Prints choice: re-total (recalcSummary refreshes its row too).
      if (e.target.matches('[data-wac-copies]')) {
        this.recalcSummary();
        return;
      }

      // Gift gate: re-total, update the saved property, revalidate.
      if (e.target.matches('[data-wac-gift-toggle]')) {
        this.recalcSummary();
        this.setGiftProp();
        this.revalidatePanel(e.target.closest('[data-wac-panel]'));
        return;
      }

      if (!e.target.matches('input[type="radio"]')) return;

      // Any selection clears a pending validation error on its step.
      var changedPanel = e.target.closest('[data-wac-panel]');
      if (changedPanel && changedPanel.classList.contains('has-error')) {
        this.clearError(changedPanel);
      }
      // Only variant option panels drive variant matching.
      if (!e.target.closest('[data-wac-option-position]')) return;

      var selected = this.getSelectedOptions();
      var variant = this.matchExact(selected);

      // No exact combo: fall back to the changed option alone, preferring available.
      if (!variant) {
        var panel = e.target.closest('[data-wac-option-position]');
        if (panel) {
          var pos = parseInt(panel.getAttribute('data-wac-option-position'), 10) - 1;
          var val = e.target.value;
          variant =
            this.variants.filter(function (v) { return v.o[pos] === val && v.a; })[0] ||
            this.variants.filter(function (v) { return v.o[pos] === val; })[0] ||
            null;
        }
      }

      if (variant) this.apply(variant);
    }

    apply(variant) {
      this.current = variant;
      this.baseline = variant.o.slice();
      this.syncOptionRadios(variant);
      this.refreshAvailability();
      this.updateSelectedNames(variant);
      this.updatePrice(variant);
      this.updateGallery(variant);
      this.updateURL(variant);
      this.recalcSummary();

      var idInput = this.querySelector('[data-wac-id]');
      if (idInput) idInput.value = variant.id;
    }

    /* --- Running-total summary --- */

    // base + each checked extra (and gift) = live total; rows follow their toggles.
    // Extra prints selected (0 when the step is off or "just one").
    copiesCount() {
      var inputs = this.copyInputs || [];
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].checked) return parseInt(inputs[i].getAttribute('data-copies-count'), 10) || 0;
      }
      return 0;
    }

    // Show/hide + fill the "Extra prints" summary row (extra prints × base price).
    updateCopiesRow() {
      var row = this.copiesRow;
      if (!row) return;
      var count = this.copiesCount();
      if (count > 0) {
        var each = this.current ? this.current.p : 0;
        var val = row.querySelector('[data-wac-copies-value]');
        if (val) val.textContent = '+' + this.money(each * count);
        row.hidden = false;
      } else {
        row.hidden = true;
      }
    }

    recalcSummary() {
      var base = this.current ? this.current.p : 0;
      // "was" line = the line's compare-at when it's higher than its price, else the price.
      var baseCompare = (this.current && this.current.c && this.current.c > base) ? this.current.c : base;
      var copies = 1 + this.copiesCount();
      // Each extra print is the same artwork at the same price (quantity model).
      var total = base * copies;
      var wasCents = baseCompare * copies;
      var self = this;

      if (this.giftToggle) {
        var giftOn = !!this.giftToggle.checked;
        if (giftOn) {
          var gp = parseInt(this.giftToggle.getAttribute('data-wac-gift-price'), 10) || 0;
          var gc = parseInt(this.giftToggle.getAttribute('data-wac-gift-compare'), 10) || 0;
          total += gp;
          wasCents += gc > gp ? gc : gp;
        }
        if (this.giftSummaryRow) this.giftSummaryRow.hidden = !giftOn;
      }

      this.extraInputs.forEach(function (input) {
        var on = !!input.checked;
        var price = parseInt(input.getAttribute('data-wac-extra-price'), 10) || 0;
        var comp = parseInt(input.getAttribute('data-wac-extra-compare'), 10) || 0;
        if (on) {
          total += price;
          wasCents += comp > price ? comp : price;
        }
        var id = input.getAttribute('data-wac-extra-id');
        var row = id && self.querySelector('[data-wac-summary-extra="' + id + '"]');
        if (row) row.hidden = !on;
      });

      // Fast Track lives in the sibling next-batch block (assets/fast-track.js).
      var ftOn = !!(window.FastTrack && window.FastTrack.isOn(this.section));
      if (ftOn) {
        var ftp = window.FastTrack.priceCents(this.section);
        total += ftp;
        wasCents += ftp; // no compare-at on Fast Track — contributes its price to both
      }
      if (this.ftSummaryRow) this.ftSummaryRow.hidden = !ftOn;

      if (this.baseEl) this.baseEl.textContent = this.money(base);
      if (this.totalEl) this.totalEl.textContent = this.money(total);
      this.updateWasPrice(total, wasCents);
      this.updateCopiesRow();
      this.updateFreeship(total);
    }

    // Compare-at "was" total + saving beside the Total — only when the setting is
    // on (the elements exist) and there is an actual discount (was > now).
    updateWasPrice(subCents, wasCents) {
      if (!this.compareHolder) return;
      var hasDiscount = wasCents > subCents;
      this.compareHolder.hidden = !hasDiscount;
      if (this.saveHolder) this.saveHolder.hidden = !hasDiscount;
      if (!hasDiscount) return;
      if (this.compareMoney) this.compareMoney.textContent = this.money(wasCents);
      if (this.saveMoney) this.saveMoney.textContent = this.money(wasCents - subCents);
    }

    /* --- Free-shipping progress --- */

    // Render the away/unlocked message from total vs threshold; hidden if no threshold.
    updateFreeship(totalCents) {
      var el = this.freeshipEl;
      if (!el) return;
      var threshold = window.__themeFreeShippingThresholdCents;
      threshold = parseInt(threshold, 10);
      if (isNaN(threshold) || threshold <= 0) { el.hidden = true; return; }
      el.hidden = false;

      var remaining = threshold - totalCents;
      var tmpl, amountHtml;
      if (remaining > 0) {
        tmpl = el.getAttribute('data-wac-freeship-away') ||
          "You're only [amount] away from **FREE SHIPPING**";
        amountHtml = '<span class="wac__freeship-amount">' +
          this.escapeHtml(this.money(remaining)) + '</span>';
      } else {
        tmpl = el.getAttribute('data-wac-freeship-unlocked') ||
          "Congrats! You've earned **FREE SHIPPING**";
        amountHtml = '';
      }
      el.innerHTML = this.renderFreeshipTemplate(tmpl, amountHtml);
    }

    // Escape the merchant template, then apply **bold** and the [amount]/{{amount}} token.
    renderFreeshipTemplate(tmpl, amountHtml) {
      var text = this.escapeHtml(String(tmpl));
      text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      text = text.replace(/\[amount\]|\{\{\s*amount\s*\}\}/g, amountHtml);
      return text;
    }

    escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return {
          '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[c];
      });
    }

    /* --- Gift message: live counter + preview + saved property --- */

    onInput(e) {
      if (!e.target.matches || !e.target.matches('[data-wac-gift-message]')) return;
      var value = e.target.value;
      if (this.giftCountEl) this.giftCountEl.textContent = value.length;
      if (this.giftPreviewEl) {
        var ph = this.giftPreviewEl.getAttribute('data-placeholder') || '';
        var empty = value.trim() === '';
        this.giftPreviewEl.textContent = empty ? ph : value;
        this.giftPreviewEl.classList.toggle('is-empty', empty);
      }
      this.setGiftProp();
      this.revalidatePanel(e.target.closest('[data-wac-panel]'));
    }

    // Clear a panel's error once its step becomes valid.
    revalidatePanel(panel) {
      if (!panel || !panel.classList.contains('has-error')) return;
      var idx = this.panels.indexOf(panel);
      if (idx !== -1 && this.stepError(idx) === null) this.clearError(panel);
    }

    // Carry the message only while the gate is checked and non-empty (else disabled).
    setGiftProp() {
      var el = this.querySelector('[data-wac-prop="gift"]');
      if (!el) return;
      var on = this.giftToggle && this.giftToggle.checked;
      var msg = this.giftMessageEl ? this.giftMessageEl.value.trim() : '';
      if (on && msg) {
        el.value = msg;
        el.disabled = false;
      } else {
        el.value = '';
        el.disabled = true;
      }
    }

    // Keep ?variant=<id> in the URL so refresh/share restores the selection.
    updateURL(variant) {
      if (!variant || this.dataset.wacUpdateUrl === 'false') return;
      if (!window.history || !window.history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        /* no-op — URL syncing is a progressive enhancement */
      }
    }

    // Grey out + disable option values that can't reach an available variant.
    refreshAvailability() {
      if (!this.variants || !this.variants.length) return;
      var self = this;
      var selected = this.getSelectedOptions();
      var panels = this.querySelectorAll('[data-wac-panel][data-wac-option-position]');
      Array.prototype.forEach.call(panels, function (panel) {
        var pos = parseInt(panel.getAttribute('data-wac-option-position'), 10) - 1;
        var radios = panel.querySelectorAll('input[type="radio"]');
        Array.prototype.forEach.call(radios, function (input) {
          var label = input.closest('[data-value]');
          var val = input.value;
          var reachable = self.variants.some(function (v) {
            if (!v.a || v.o[pos] !== val) return false;
            for (var k = 0; k < selected.length; k++) {
              if (k === pos || selected[k] == null) continue;
              if (v.o[k] !== selected[k]) return false;
            }
            return true;
          });
          if (label) label.classList.toggle('is-unavailable', !reachable);
          input.disabled = !reachable && !input.checked;
        });
      });
    }

    // Re-check every option radio to match the applied variant.
    syncOptionRadios(variant) {
      var panels = this.querySelectorAll('[data-wac-panel][data-wac-option-position]');
      Array.prototype.forEach.call(panels, function (panel) {
        var pos = parseInt(panel.getAttribute('data-wac-option-position'), 10) - 1;
        var want = variant.o[pos];
        var radios = panel.querySelectorAll('input[type="radio"]');
        Array.prototype.forEach.call(radios, function (radio) {
          radio.checked = radio.value === want;
        });
      });
    }

    updateSelectedNames(variant) {
      var panels = this.querySelectorAll('[data-wac-panel][data-wac-option-position]');
      Array.prototype.forEach.call(panels, function (panel) {
        var pos = parseInt(panel.getAttribute('data-wac-option-position'), 10) - 1;
        var sel = panel.querySelector('[data-wac-selected]');
        if (sel && variant.o[pos] != null) sel.textContent = variant.o[pos];
      });
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currency
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }

    // Shared with pet-portrait-price.liquid — same [data-pp-price]/[data-pp-compare] hooks.
    updatePrice(variant) {
      var now = this.section.querySelector('[data-pp-price]');
      var was = this.section.querySelector('[data-pp-compare]');
      if (now) now.textContent = this.money(variant.p);
      if (was) {
        if (variant.c && variant.c > variant.p) {
          was.textContent = this.money(variant.c);
          was.hidden = false;
        } else {
          was.hidden = true;
        }
      }
    }

    updateGallery(variant) {
      if (!variant.m) return;
      var gallery = this.section.querySelector('pet-portrait-gallery');
      if (gallery && typeof gallery.selectMedia === 'function') {
        gallery.selectMedia(variant.m);
      }
    }

    /* --- Line-item property (photo status) --- */

    // Empty value => disabled => not sent to the cart.
    setPhotoProp() {
      var el = this.querySelector('[data-wac-prop="photo"]');
      if (!el) return;
      var value = this.allPhotosDone() ? 'Uploaded' : (this._skipPhoto ? 'To email later' : '');
      if (value) {
        el.value = value;
        el.disabled = false;
      } else {
        el.value = '';
        el.disabled = true;
      }
    }

    /* --- Add to cart --- */

    // Re-validate all steps on click; jump to the first offender, else add to cart.
    onAtc(makeAnother, btn) {
      this.clearAtcError();
      var invalid = this.firstInvalidBefore(this.total);
      if (invalid !== -1) {
        // Remember intent so the "no photo yet" popup can replay it on continue.
        this._pendingMakeAnother = makeAnother;
        this._pendingAtcBtn = btn;
        this.jumpToInvalid(invalid);
        return;
      }
      this.addToCart(makeAnother, btn);
    }

    // One /cart/add.js with the main variant + gift + extras as separate lines,
    // sharing a `_Bundle` id when there are add-ons. Redirects to /cart on success.
    addToCart(makeAnother, btn) {
      if (this._adding) return;

      var idInput = this.querySelector('[data-wac-id]');
      var mainId = idInput ? parseInt(idInput.value, 10) : (this.current && this.current.id);
      if (!mainId) {
        this.showAtcError(this.dataset.wacCartError || 'Something went wrong. Please try again.');
        return;
      }

      var giftOn = !!(this.giftToggle && this.giftToggle.checked);
      var checkedExtras = this.extraInputs.filter(function (input) { return input.checked; });
      var hasAddons = giftOn || checkedExtras.length > 0;
      var bundleId = hasAddons ? this.makeBundleId() : null;

      var items = [];

      var mainProps = this.collectMainProperties();
      // Unique per-line stamp so Shopify never merges two custom pieces into one line.
      var stamp = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
      mainProps.__time = stamp;
      if (bundleId) mainProps._Bundle = bundleId;
      // Extra prints are the same artwork at the same price → one line, higher quantity.
      items.push({ id: mainId, quantity: 1 + this.copiesCount(), properties: mainProps });

      // Gift card line: the printed message rides on the card, not the artwork.
      if (giftOn) {
        var giftId = parseInt(this.giftToggle.getAttribute('data-wac-gift-variant'), 10);
        if (giftId) {
          var giftProps = { _Bundle: bundleId };
          var msg = this.giftMessageEl ? this.giftMessageEl.value.trim() : '';
          if (msg) giftProps['Gift Message'] = msg;
          items.push({ id: giftId, quantity: 1, properties: giftProps });
        }
      }

      checkedExtras.forEach(function (input) {
        var vid = parseInt(input.getAttribute('data-wac-extra-variant'), 10);
        if (vid) items.push({ id: vid, quantity: 1, properties: { _Bundle: bundleId } });
      });

      // Fast Track add-on line (logic in assets/fast-track.js).
      var ftLine = window.FastTrack && window.FastTrack.lineItem(this.section, stamp);
      if (ftLine) items.push(ftLine);

      this._adding = true;
      this.setAtcLoading(true, btn);
      this.hideAdded();
      var self = this;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          return r.json().then(function (data) { return { ok: r.ok, data: data }; });
        })
        .then(function (res) {
          if (!res.ok) {
            throw new Error((res.data && (res.data.description || res.data.message)) || '');
          }
          if (makeAnother) {
            // Stay on the page: refresh the theme cart, confirm, reset for a new piece.
            self._adding = false;
            self.setAtcLoading(false, btn);
            self.refreshCart();
            self.showAdded();
            self.resetConfigurator();
            if (typeof self.scrollIntoView === 'function') {
              self.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          } else {
            window.location.href = '/cart';
          }
        })
        .catch(function (err) {
          self._adding = false;
          self.setAtcLoading(false, btn);
          self.showAtcError(err.message || self.dataset.wacCartError || 'Something went wrong. Please try again.');
        });
    }

    // Refresh drawer + header count via the theme's reBuildCart; fallback patches .cart_trigger.
    refreshCart() {
      if (typeof window.reBuildCart === 'function') {
        try { window.reBuildCart(); return; } catch (e) { /* fall through */ }
      }
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var count = cart.item_count;
          Array.prototype.forEach.call(document.querySelectorAll('.cart_trigger'), function (n) {
            n.setAttribute('cart_item_count', count);
            n.classList.toggle('show_bubble', count > 0);
          });
        })
        .catch(function () { /* non-fatal */ });
    }

    showAdded() {
      if (!this.addedEl) return;
      this.addedEl.textContent = this.dataset.wacAddedLabel || 'Added to cart ✓';
      this.addedEl.hidden = false;
      var self = this;
      clearTimeout(this._addedTimer);
      this._addedTimer = setTimeout(function () { self.hideAdded(); }, 4000);
    }

    hideAdded() {
      if (this.addedEl) this.addedEl.hidden = true;
    }

    // Reset everything to first-render defaults for a fresh build ("Make another").
    resetConfigurator() {
      // Uncheck all radios/checkboxes, then restore the initially-checked snapshot.
      var all = this.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      Array.prototype.forEach.call(all, function (el) { el.checked = false; });
      (this._initialCheckedInputs || []).forEach(function (el) { el.checked = true; });

      // Fast Track lives in a sibling block — switch it off too.
      if (window.FastTrack) window.FastTrack.reset(this.section);

      // Re-apply the starting variant (re-syncs option radios, names, price, gallery).
      var start = this.findById(this._initialVariantId) || this.firstAvailable() || this.variants[0];
      if (start) this.apply(start);

      // Gift message: clear editor, counter, preview and the saved property.
      if (this.giftMessageEl) this.giftMessageEl.value = '';
      if (this.giftCountEl) this.giftCountEl.textContent = '0';
      if (this.giftPreviewEl) {
        var ph = this.giftPreviewEl.getAttribute('data-placeholder') || '';
        this.giftPreviewEl.textContent = ph;
        this.giftPreviewEl.classList.add('is-empty');
      }
      this.setGiftProp();

      // Photo + pet names: clear so the next piece starts fresh.
      this.resetPhotos();
      Array.prototype.forEach.call(this.querySelectorAll('[data-wac-pet-name]'), function (i) { i.value = ''; });

      // Clear errors, re-total, back to step 1.
      var self = this;
      this.panels.forEach(function (p) { self.clearError(p); });
      this.refreshAvailability();
      this.recalcSummary();
      this.step = 0;
      this.showStep(false);
      // Roll the tab strip back to the first tab (showStep(false) leaves it parked).
      if (this.tabsEl) {
        if (typeof this.tabsEl.scrollTo === 'function') this.tabsEl.scrollTo({ left: 0, behavior: 'auto' });
        else this.tabsEl.scrollLeft = 0;
      }
    }

    // Clear the app's saved upload data + card state so the photo gate resets.
    resetPhotos() {
      this._skipPhoto = false;
      Array.prototype.forEach.call(this.querySelectorAll('[name^="properties["]'), function (el) {
        if (el.type === 'radio' || el.type === 'checkbox') return;
        if (el.hasAttribute('data-wac-prop') || el.hasAttribute('data-wac-pet-name')) return;
        el.value = '';
      });
      Array.prototype.forEach.call(this.querySelectorAll('.uploadkit-uploader'), function (u) {
        u.classList.remove('has-photo');
      });
      this.setPhotoProp();
      if (this.uploadStep >= 0) this.revalidatePanel(this.panels[this.uploadStep]);
    }

    // Underscore-prefixed group id (hidden from the customer, kept on the order).
    makeBundleId() {
      return 'wac-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
    }

    // Every enabled, non-empty properties[…] input except the gift message.
    collectMainProperties() {
      var props = {};
      var inputs = this.querySelectorAll('[name^="properties["]');
      Array.prototype.forEach.call(inputs, function (el) {
        if (el.disabled) return;
        if (el.getAttribute('data-wac-prop') === 'gift') return;
        // Radio/checkbox groups (e.g. Extra Prints) share one name — only the checked one counts.
        if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
        var m = el.name.match(/^properties\[(.+)\]$/);
        if (!m) return;
        if (el.value == null || el.value === '') return;
        props[m[1]] = el.value;
      });
      return props;
    }

    setAtcLoading(on, btn) {
      var active = btn || this.atcBtn;
      var adding = this.dataset.wacAddingLabel || 'Adding…';
      (this.atcBtns || []).forEach(function (b) {
        b.disabled = on;
        b.classList.toggle('is-loading', on && b === active);
      });
      if (!active) return;
      if (on) {
        active._label = active.textContent;
        active.textContent = adding;
      } else if (active._label != null) {
        active.textContent = active._label;
      }
    }

    showAtcError(message) {
      var el = this.querySelector('[data-wac-atc-error]');
      if (!el) return;
      el.textContent = message || this.dataset.wacCartError || 'Something went wrong. Please try again.';
      el.hidden = false;
    }

    clearAtcError() {
      var el = this.querySelector('[data-wac-atc-error]');
      if (el) el.hidden = true;
    }

    /* --- Photo upload (owned by the store's UploadKit / Uploadcare app) --- */

    // The uploader buttons the app injected (count owned by the app, not the theme).
    uploadButtons() {
      return Array.prototype.slice.call(this.querySelectorAll('.uploadkit-button'));
    }

    // An uploader is "done" once its "(direct link)" property input has a value.
    isButtonDone(btn) {
      var name = btn && btn.getAttribute('data-uploader-name');
      if (!name) return false;
      var direct = this.querySelector('input[name="properties[_' + name + ' (direct link)]"]');
      return !!(direct && direct.value);
    }

    // Satisfied when every uploader has a photo. Zero buttons counts as satisfied
    // ONLY after hydration (_uploadHydrated) — never during the app's load window,
    // else the gate could be skipped before the buttons appear.
    allPhotosDone() {
      var btns = this.uploadButtons();
      if (!btns.length) return this._uploadHydrated === true;
      var self = this;
      return btns.every(function (btn) { return self.isButtonDone(btn); });
    }

    // Open the first uploader missing a photo (falls back to the first).
    openFirstPendingUploader() {
      var btns = this.uploadButtons();
      var self = this;
      var pending = btns.filter(function (btn) { return !self.isButtonDone(btn); })[0];
      var target = pending || btns[0];
      if (target) target.click();
      else this.ensureUploader();
    }

    // Poll until the app injects a .uploadkit-button, then observe for uploads.
    // (The app hydrates the host even while the Upload step is display:none.)
    ensureUploader() {
      if (this._uploaderReady || this._uploadPoll) return;
      var kit = this.querySelector('.uploadkit');
      // No host — no uploader possible; mark hydrated so the step isn't blocked.
      if (!kit) { this._uploadHydrated = true; return; }
      var self = this;
      var tries = 0;
      this._uploadPoll = setInterval(function () {
        tries++;
        if (self.uploadButtons().length > 0) {
          clearInterval(self._uploadPoll);
          self._uploadPoll = null;
          self.bindUploaders();
          return;
        }
        if (tries > 60) { // ~18s — app rendered no uploader; now "no photo required".
          clearInterval(self._uploadPoll);
          self._uploadPoll = null;
          self._uploadHydrated = true;
          self.setPhotoProp();
          if (self.uploadStep >= 0) self.revalidatePanel(self.panels[self.uploadStep]);
        }
      }, 300);
    }

    // Observe the host so the photo gate + status stay live on any upload change.
    bindUploaders() {
      this._uploaderReady = true;
      this._uploadHydrated = true;
      var self = this;
      var host = this.querySelector('.uploadkit') || this;
      if (window.MutationObserver && !this._uploadObserver) {
        this._uploadObserver = new MutationObserver(function () { self.readAllUploads(); });
        this._uploadObserver.observe(host, { childList: true, subtree: true, attributes: true });
      }
      this.readAllUploads();
    }

    // One pet-name input per uploader (1 photo = 1 name), placed inside its
    // .uploadkit-uploader so photo + name read as one card. Idempotent: created
    // once per uploader (typed values survive re-renders), label kept in sync.
    renderPetNameFields() {
      var cfg = this.querySelector('[data-wac-names]');
      var label = (cfg && cfg.getAttribute('data-wac-name-label')) || 'Pet Name';
      var ph = (cfg && cfg.getAttribute('data-wac-name-placeholder')) || '';
      var sectionId = (this.section && this.section.id) || 'wac';

      var uploaders = Array.prototype.slice.call(
        this.querySelectorAll('.uploadkit-uploader')
      );
      var multi = uploaders.length > 1;

      uploaders.forEach(function (up, idx) {
        var n = idx + 1;
        var text = multi ? label + ' ' + n : label;

        // Field exists — sync the label only (writing only on change avoids an
        // observer → re-render loop), leave the input untouched.
        var existing = up.querySelector('.wac-upload__name-label');
        if (existing) {
          if (existing.textContent !== text) existing.textContent = text;
          return;
        }

        var id = 'wac-petname-' + sectionId + '-' + n;
        var field = document.createElement('div');
        field.className = 'wac-field wac-upload__name';

        var lbl = document.createElement('label');
        lbl.className = 'wac-field__label wac-upload__name-label';
        lbl.setAttribute('for', id);
        lbl.textContent = text;

        var input = document.createElement('input');
        input.type = 'text';
        input.id = id;
        input.className = 'wac-field__input wac-pet-name';
        input.name = 'properties[_text_pet_name_' + n + ']';
        input.placeholder = ph;
        input.setAttribute('maxlength', '60');
        input.setAttribute('autocomplete', 'off');
        input.setAttribute('data-wac-pet-name', '');

        field.appendChild(lbl);
        field.appendChild(input);
        up.appendChild(field);
      });
    }

    readAllUploads() {
      var self = this;
      this.renderPetNameFields(); // uploaders can arrive incrementally
      var any = false;
      this.uploadButtons().forEach(function (btn) {
        var done = self.isButtonDone(btn);
        var wrap = btn.closest('.uploadkit-uploader') || btn;
        if (wrap && wrap.classList) wrap.classList.toggle('has-photo', done);
        if (done) any = true;
      });
      if (any) this._skipPhoto = false;
      this.setPhotoProp();
      if (this.uploadStep >= 0) this.revalidatePanel(this.panels[this.uploadStep]);
    }

    /* --- Overlays: Photo Guide modal + confirm popup --- */

    openOverlay(el) {
      if (!el) return;
      el.hidden = false;
      void el.offsetWidth; // force reflow so the fade-in runs
      el.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      var closeBtn = el.querySelector('.wac-modal__close');
      if (closeBtn) closeBtn.focus();
    }

    closeOverlay(el) {
      if (!el) return;
      el.classList.remove('is-open');
      if (!this.querySelector('.wac-modal.is-open')) {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }
      window.setTimeout(function () {
        if (!el.classList.contains('is-open')) el.hidden = true;
      }, 200);
    }
  }

  if (window.customElements && !window.customElements.get(TAG)) {
    window.customElements.define(TAG, WallArtConfigurator);
  }
})();
